import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { freelancerService } from '../services/api';
import { Freelancer } from '../types';
import { 
  ArrowLeft, 
  Star, 
  Clock, 
  DollarSign, 
  Globe, 
  Mail, 
  Phone, 
  MapPin, 
  Calendar,
  Award,
  Users,
  CheckCircle,
  ExternalLink,
  MessageCircle,
  Heart,
  Share2,
  AlertCircle
} from 'lucide-react';

const FreelancerProfile: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [freelancer, setFreelancer] = useState<Freelancer | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFavorite, setIsFavorite] = useState(false);

  useEffect(() => {
    if (id) {
      loadFreelancerProfile();
    }
  }, [id]);

  const loadFreelancerProfile = async () => {
    try {
      // For now, we'll get all freelancers and find the one by ID
      // In a real app, you'd have a specific endpoint for this
      const allFreelancers = await freelancerService.getAll();
      const found = allFreelancers.find(f => f.id === id);
      if (found) {
        setFreelancer(found);
      } else {
        setError('Freelancer not found');
      }
    } catch (err) {
      setError('Failed to load freelancer profile');
      console.error('Error loading freelancer:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleContact = () => {
    // In a real app, this would open a contact form or messaging system
    window.open(`mailto:${freelancer?.email}`, '_blank');
  };

  const handleFavorite = () => {
    setIsFavorite(!isFavorite);
    // In a real app, this would save to backend
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: `${freelancer?.name} - Freelancer Profile`,
        text: `Check out ${freelancer?.name}'s profile on RenderSpace`,
        url: window.location.href,
      });
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      alert('Profile link copied to clipboard!');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (error || !freelancer) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <AlertCircle size={40} className="mx-auto text-red-500 mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error</h2>
          <p className="text-gray-600 mb-4">{error || 'Freelancer not found'}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="bg-gray-800/50 backdrop-blur-sm border-b border-white/10 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link
                to="/freelancers"
                className="flex items-center space-x-2 text-white/70 hover:text-white"
              >
                <ArrowLeft size={20} />
                <span>Back to Freelancers</span>
              </Link>
              <div>
                <h1 className="text-2xl font-bold text-white">Freelancer Profile</h1>
                <p className="text-white/70">View detailed information about this professional</p>
              </div>
            </div>
            <div className="flex space-x-3">
              <button className="bg-primary-600 hover:bg-primary-700 text-white font-medium py-2 px-4 rounded-md transition-colors flex items-center space-x-2">
                <MessageCircle size={16} />
                <span>Contact</span>
              </button>
              <button className="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-md transition-colors flex items-center space-x-2">
                <DollarSign size={16} />
                <span>Hire</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-gray-800/50 backdrop-blur-sm border border-white/10 rounded-lg shadow-lg p-6 sticky top-8">
              <div className="text-center mb-6">
                <div className="w-24 h-24 bg-primary-500/20 rounded-full flex items-center justify-center mx-auto mb-4 border border-primary-500/30">
                  <span className="text-3xl font-bold text-primary-400">
                    {freelancer.name.charAt(0).toUpperCase()}
                  </span>
                </div>
                <h2 className="text-xl font-semibold text-white mb-2">{freelancer.name}</h2>
                <p className="text-white/70 text-sm">{freelancer.email}</p>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-white/60">Cost per Project</span>
                  <span className="font-semibold text-white">${freelancer.cost_per_project}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-white/60">Experience</span>
                  <span className="font-semibold text-white">{freelancer.experience_years} years</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-white/60">Availability</span>
                  <div className={`flex items-center space-x-1 ${
                    freelancer.availability ? 'text-green-400' : 'text-red-400'
                  }`}>
                    <div className={`w-2 h-2 rounded-full ${
                      freelancer.availability ? 'bg-green-400' : 'bg-red-400'
                    }`} />
                    <span className="text-sm">
                      {freelancer.availability ? 'Available' : 'Not Available'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-white/10">
                <button className="w-full bg-primary-600 hover:bg-primary-700 text-white font-medium py-3 px-4 rounded-md transition-colors flex items-center justify-center space-x-2 mb-3">
                  <MessageCircle size={16} />
                  <span>Contact {freelancer.name}</span>
                </button>
                <button className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-3 px-4 rounded-md transition-colors flex items-center space-x-2">
                  <DollarSign size={16} />
                  <span>Hire Now</span>
                </button>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Profile Header */}
            <div className="bg-gray-800/50 backdrop-blur-sm border border-white/10 rounded-lg shadow-lg p-6">
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-center space-x-4">
                  <div className="w-20 h-20 bg-primary-500/20 rounded-full flex items-center justify-center border border-primary-500/30">
                    <span className="text-2xl font-bold text-primary-400">
                      {freelancer.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold text-white">{freelancer.name}</h1>
                    <p className="text-white/70">{freelancer.email}</p>
                    <div className="flex items-center space-x-4 mt-2">
                      <div className="flex items-center space-x-1">
                        <Star size={16} className="text-yellow-400" />
                        <span className="text-sm text-white/70">4.8 (127 reviews)</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <CheckCircle size={16} className="text-green-400" />
                        <span className="text-sm text-white/70">Verified</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-primary-400">
                    ${freelancer.cost_per_project}
                  </div>
                  <div className={`flex items-center space-x-1 mt-1 ${
                    freelancer.availability ? 'text-green-400' : 'text-red-400'
                  }`}>
                    <div className={`w-2 h-2 rounded-full ${
                      freelancer.availability ? 'bg-green-400' : 'bg-red-400'
                    }`} />
                    <span className="text-sm">
                      {freelancer.availability ? 'Available' : 'Not Available'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-3 gap-4 py-4 border-t border-white/10">
                <div className="text-center">
                  <div className="text-lg font-semibold text-white">98%</div>
                  <div className="text-sm text-white/70">Success Rate</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-semibold text-white">127</div>
                  <div className="text-sm text-white/70">Projects Completed</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-semibold text-white">2.1 days</div>
                  <div className="text-sm text-white/70">Avg. Response Time</div>
                </div>
              </div>
            </div>

            {/* About */}
            <div className="bg-gray-800/50 backdrop-blur-sm border border-white/10 rounded-lg shadow-lg p-6">
              <h2 className="text-xl font-semibold text-white mb-4">About</h2>
              <p className="text-white/80 leading-relaxed">
                {freelancer.bio || `Experienced ${freelancer.skills.join(', ')} professional with ${freelancer.experience_years} years of expertise. Passionate about delivering high-quality solutions and exceeding client expectations.`}
              </p>
            </div>

            {/* Skills */}
            <div className="bg-gray-800/50 backdrop-blur-sm border border-white/10 rounded-lg shadow-lg p-6">
              <h2 className="text-xl font-semibold text-white mb-4">Skills & Expertise</h2>
              <div className="flex flex-wrap gap-2">
                {freelancer.skills.map((skill, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-primary-500/20 text-primary-300 rounded-full text-sm font-medium border border-primary-500/30"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </div>

            {/* Experience */}
            <div className="bg-gray-800/50 backdrop-blur-sm border border-white/10 rounded-lg shadow-lg p-6">
              <h2 className="text-xl font-semibold text-white mb-4">Experience</h2>
              <div className="space-y-4">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-primary-500/20 rounded-lg flex items-center justify-center flex-shrink-0 border border-primary-500/30">
                    <Award size={20} className="text-primary-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">Senior Developer</h3>
                    <p className="text-white/70">Tech Company • 2020 - Present</p>
                    <p className="text-white/80 mt-1">
                      Led development of multiple web applications using modern technologies.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-primary-500/20 rounded-lg flex items-center justify-center flex-shrink-0 border border-primary-500/30">
                    <Award size={20} className="text-primary-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">Full Stack Developer</h3>
                    <p className="text-white/70">Startup • 2018 - 2020</p>
                    <p className="text-white/80 mt-1">
                      Built scalable web applications and APIs for growing businesses.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Portfolio */}
            {freelancer.portfolio_url && (
              <div className="bg-gray-800/50 backdrop-blur-sm border border-white/10 rounded-lg shadow-lg p-6">
                <h2 className="text-xl font-semibold text-white mb-4">Portfolio</h2>
                <a
                  href={freelancer.portfolio_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center space-x-2 text-primary-400 hover:text-primary-300 font-medium"
                >
                  <ExternalLink size={16} />
                  <span>View Portfolio</span>
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FreelancerProfile; 